import { useEffect, useMemo } from 'react'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchOfferTags } from 'actions/OfferActions'
import useQueryParams from 'hooks/useQueryParams'
import { buildOfferTagsCacheKey } from 'lib/experiences/experienceUtils'

export interface UseOfferTagsOptions {
  tagType?: App.OfferTagType
  includeAssignments?: boolean
  includedAssignmentEntityTypeIn?: Array<App.OfferTagAssignment['entityType']>
}

interface UseOfferTagsResult {
  tags: Array<App.OfferTag>
  fetching: boolean
}

export interface UseOfferTagsForOffersResult {
  tagsByOfferId: Record<string, Array<App.OfferTag>>
  fetchingByOfferId: Record<string, boolean>
}

const EMPTY_OFFER_TAGS: Array<App.OfferTag> = []

function useOfferTags(
  offerId: string | undefined,
  options: UseOfferTagsOptions = {},
): UseOfferTagsResult {
  const offerIds = useMemo(() => offerId ? [offerId] : [], [offerId])
  const { tagsByOfferId, fetchingByOfferId } = useOfferTagsForOffers(offerIds, options)

  return {
    tags: offerId ? tagsByOfferId[offerId] ?? EMPTY_OFFER_TAGS : EMPTY_OFFER_TAGS,
    fetching: offerId ? fetchingByOfferId[offerId] ?? true : false,
  }
}

/**
 * Base tag hook for one or more offers. useOfferTags delegates here so the
 * cache key and fetch behaviour have a single implementation.
 */
export function useOfferTagsForOffers(
  offerIds: Array<string>,
  options: UseOfferTagsOptions = {},
  filters?: { checkIn?: string, checkOut?: string },
): UseOfferTagsForOffersResult {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()
  const { tagType, includeAssignments, includedAssignmentEntityTypeIn } = options
  const checkInDate = filters?.checkIn ?? queryParams.get('checkIn') ?? undefined
  const checkOutDate = filters?.checkOut ?? queryParams.get('checkOut') ?? undefined
  const uniqueOfferIds = useMemo(() => Array.from(new Set(offerIds)), [offerIds])
  const offerIdsKey = uniqueOfferIds.join(',')

  const cacheKeysByOfferId = useMemo(() => Object.fromEntries(uniqueOfferIds.map(offerId => [
    offerId,
    buildOfferTagsCacheKey(
      offerId,
      checkInDate,
      checkOutDate,
      tagType,
      includeAssignments,
      includedAssignmentEntityTypeIn,
    ),
  ])), [uniqueOfferIds, checkInDate, checkOutDate, tagType, includeAssignments, includedAssignmentEntityTypeIn])

  const tagData = useAppSelector(state => uniqueOfferIds.map(offerId =>
    state.offer.offerTags?.[cacheKeysByOfferId[offerId]],
  ), shallowEqual)
  const tagDataByOfferId = useMemo(() => Object.fromEntries(uniqueOfferIds.map((offerId, index) => [
    offerId,
    tagData[index],
  ])), [tagData, uniqueOfferIds])

  useEffect(() => {
    for (const offerId of uniqueOfferIds) {
      dispatch(fetchOfferTags(offerId, {
        travelStartDate: checkInDate,
        travelEndDate: checkOutDate,
        travelDateOverlap: true,
        tagType,
        includeAssignments,
        includedAssignmentEntityTypeIn,
      }))
    }
  }, [dispatch, offerIdsKey, checkInDate, checkOutDate, tagType, includeAssignments, includedAssignmentEntityTypeIn, uniqueOfferIds])

  return useMemo(() => ({
    tagsByOfferId: Object.fromEntries(uniqueOfferIds.map(offerId => [
      offerId,
      tagDataByOfferId[offerId]?.tags ?? EMPTY_OFFER_TAGS,
    ])),
    fetchingByOfferId: Object.fromEntries(uniqueOfferIds.map(offerId => [
      offerId,
      tagDataByOfferId[offerId]?.fetching ?? true,
    ])),
  }), [uniqueOfferIds, tagDataByOfferId])
}

export default useOfferTags
